/**
 * ทะเบียนเครื่องสแกน Android (Handheld Device) — D-074, D-079, D-176
 *
 * ADMIN เท่านั้น (`DEVICE_REGISTRY` + VIEW ที่ระดับ route; ลงทะเบียน = CREATE, revoke = EDIT ถูกกันข้างใน)
 * เครื่องที่ revoke แล้วไม่ถูกลบ — คำสั่งที่เคยส่งต้องตามกลับมาถึงเครื่องได้ จึงมีแต่ปุ่ม "เพิกถอน" ไม่มีปุ่มลบ
 * `device_key` เป็น public key/opaque token ของเครื่อง: กรอกตอนลงทะเบียนเท่านั้น และไม่แสดงกลับ
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { StatusBadge } from '../StatusBadge';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { stockFailureText } from '../../lib/inventory/stock';
import {
  fetchDevices,
  registerDevice,
  revokeDevice,
  type RegisteredDevice,
} from '../../lib/devices/client';

const DEVICE_MODULE = 'DEVICE_REGISTRY';

export interface DeviceRegistryScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function DeviceRegistryScreen({ permissions, apiBaseUrl, fetchImpl }: DeviceRegistryScreenProps) {
  const canCreate = can(permissions, DEVICE_MODULE, 'CREATE');
  const canRevoke = can(permissions, DEVICE_MODULE, 'EDIT');
  const options = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  const [statusFilter, setStatusFilter] = useState('');
  const [devices, setDevices] = useState<readonly RegisteredDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ plant_code: '', work_center_code: '', device_key: '' });

  const reload = useCallback(async () => {
    const result = await fetchDevices(statusFilter ? { status: statusFilter } : {}, options);
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    setDevices(result.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, apiBaseUrl, fetchImpl]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (!form.plant_code.trim() || !form.device_key.trim()) {
      setError('กรอกรหัสโรงงานและ device key');
      return;
    }
    setBusy(true);
    const result = await registerDevice(
      {
        plant_code: form.plant_code.trim().toUpperCase(),
        device_key: form.device_key.trim(),
        ...(form.work_center_code.trim() ? { work_center_code: form.work_center_code.trim().toUpperCase() } : {}),
      },
      options,
    );
    setBusy(false);
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    setNotice(`ลงทะเบียนเครื่องแล้ว: ${result.device.device_id}`);
    setForm({ plant_code: '', work_center_code: '', device_key: '' });
    void reload();
  };

  const revoke = async (device: RegisteredDevice) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    const result = await revokeDevice(device.device_id, options);
    setBusy(false);
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    setNotice(`เพิกถอนเครื่อง ${device.device_id.slice(0, 8)} แล้ว — เครื่องนี้ซิงก์ไม่ได้อีก`);
    void reload();
  };

  return (
    <div className="cap-page" data-testid="device-registry">
      <div className="cap-page__head">
        <h1 className="cap-page__title">เครื่องสแกน Android (Handheld Device)</h1>
      </div>

      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            aria-label="สถานะ"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">ทั้งหมด</option>
            <option value="ACTIVE">ใช้งานอยู่ (ACTIVE)</option>
            <option value="REVOKED">เพิกถอนแล้ว (REVOKED)</option>
          </select>
        </label>
      </div>

      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="device-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="cap-notice__text" role="status" data-testid="device-notice">
          {notice}
        </p>
      )}

      <table className="cap-table" data-testid="device-table">
        <thead>
          <tr>
            <th>เครื่อง</th>
            <th>โรงงาน</th>
            <th>Work Center</th>
            <th>สถานะ</th>
            <th>ลงทะเบียนเมื่อ</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {devices.length === 0 && (
            <tr>
              <td colSpan={6}>ยังไม่มีเครื่องที่ลงทะเบียน</td>
            </tr>
          )}
          {devices.map((device) => (
            <tr key={device.device_id}>
              <td>
                <code className="cap-table__code">{device.device_id.slice(0, 8)}</code>
              </td>
              <td>{device.plant_code}</td>
              <td>{device.work_center_code ?? '—'}</td>
              <td>
                <StatusBadge tone={device.status === 'ACTIVE' ? 'success' : 'neutral'}>{device.status}</StatusBadge>
              </td>
              <td>{device.registered_at}</td>
              <td>
                {device.status === 'ACTIVE' && canRevoke && (
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    disabled={busy}
                    aria-label={`เพิกถอนเครื่อง ${device.device_id.slice(0, 8)}`}
                    onClick={() => void revoke(device)}
                  >
                    เพิกถอน
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {canCreate && (
        <form className="cap-form" data-testid="device-form" onSubmit={(e) => void submit(e)}>
          <h2 className="cap-notice__title">ลงทะเบียนเครื่องใหม่</h2>
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              aria-label="รหัสโรงงาน"
              value={form.plant_code}
              onChange={(e) => setForm((c) => ({ ...c, plant_code: e.target.value }))}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">Work Center (ไม่บังคับ)</span>
            <input
              className="cap-input"
              aria-label="Work Center"
              value={form.work_center_code}
              onChange={(e) => setForm((c) => ({ ...c, work_center_code: e.target.value }))}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">Device key (public key / token ที่เครื่องสร้าง)</span>
            <input
              className="cap-input"
              aria-label="Device key"
              value={form.device_key}
              onChange={(e) => setForm((c) => ({ ...c, device_key: e.target.value }))}
            />
          </label>
          <div className="cap-form__actions">
            <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
              ลงทะเบียน
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
